import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import teacherService from '../../services/api/teacher';
import studentService from '../../services/api/student';
import { useAuth } from '../../contexts/AuthContext';
import { useSettings } from '../../contexts/SettingsContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useToast } from '../../components/common/Toast';
import ExploreCoursesCard from '../../components/cards/ExploreCoursesCard';
import { 
  User, 
  Video, 
  Mail, 
  Phone, 
  MapPin, 
  Star, 
  ShieldCheck, 
  ArrowRight, 
  BookOpen,
  PlayCircle, 
  GraduationCap,
  Info,
  MessageCircle,
  CheckCircle,
  Target,
  Lightbulb,
  Heart,
  ChevronLeft,
  Sparkles,
  Globe,
  Share2,
  Layers,
  HelpCircle,
  X
} from 'lucide-react';
import { getImageUrl } from '../../config/apiConfig';

// دالة معالجة روابط اليوتيوب لضمان التشغيل داخل الـ Iframe
const getYouTubeEmbedUrl = (url) => {
  if (!url) return null;
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
  const match = url.match(regExp);
  const videoId = (match && match[2].length === 11) ? match[2] : null;
  return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}?autoplay=0&rel=0&modestbranding=1&origin=${window.location.origin}` : null;
};

// دالة حساب مدة الكورس (ترجع الثواني)
const calculateTotalCourseDuration = (course) => {
  if (!course) return 0;
  
  // الأولوية الأولى: استخدام total_duration_seconds من الدروس (المحسوبة من الـ Backend)
  if (course.total_duration_seconds && parseInt(course.total_duration_seconds) > 0) {
    return parseInt(course.total_duration_seconds);
  }
  
  // الأولوية الثانية: استخدام duration_hours و duration_minutes من الكورس نفسه
  const hours = parseInt(course.duration_hours) || 0;
  const minutes = parseInt(course.duration_minutes) || 0;
  const totalFromCourse = (hours * 3600) + (minutes * 60);
  
  if (totalFromCourse > 0) {
    return totalFromCourse;
  }
  
  return 0;
};

// دالة تنسيق عرض المدة
const formatTotalDuration = (course) => {
  if (!course) return '--:--';
  const totalSeconds = calculateTotalCourseDuration(course);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  
  if (hours > 0 && minutes > 0) return `${hours}س ${minutes}د`;
  if (hours > 0 && seconds > 0) return `${hours}س ${seconds}ث`;
  if (hours > 0) return `${hours}س`;
  if (minutes > 0 && seconds > 0) return `${minutes}د ${seconds}ث`;
  if (minutes > 0) return `${minutes}د`;
  if (seconds > 0) return `${seconds}ث`;
  return '--:--';
};

// دالة تحويل الأيام إلى نص مدة الاشتراك
const getDurationTextFromDays = (days) => {
  if (!days) return "اشتراك شهرياً";
  if (days === 30) return "شهري";
  if (days === 120) return "ترم دراسي";
  if (days === 300) return "عام دراسي";
  if (days < 30) return `${days} يوم`;
  if (days % 30 === 0) return `${days / 30} شهور`;
  return `${days} يوم`;
};

const TeacherProfile = () => {
  const { teacherId } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('about');
  const { user } = useAuth(); // الحصول على معلومات المستخدم
  const { getWhatsAppLink } = useSettings();
  const toast = useToast(); // ✅ إضافة نظام Toast
  const queryClient = useQueryClient(); // إضافة query client
  
  // State للتقييم
  const [ratingModal, setRatingModal] = useState({ 
    isOpen: false, 
    type: 'teacher', // 'teacher' or 'lesson'
    lessonId: null,
    currentRating: 0,
    review: ''
  });

  // جلب تفاصيل المدرس (المنطق الأصلي كاملاً)
  const { data: teacherData, isLoading, error, refetch: refetchTeacher } = useQuery(
    ['teacher', teacherId],
    () => teacherService.getTeacherProfile(teacherId),
    { retry: 1 }
  );

  const teacher = teacherData?.teacher || {};

  // جلب كورسات المدرس
  const { data: coursesData } = useQuery(
    ['teacher-courses', teacherId],
    () => teacherService.getPublicTeacherCourses(teacherId)
  );

  const courses = coursesData?.data?.courses || coursesData?.courses || coursesData || [];

  // Fetch lessons for each course to compute analytics
  const { data: coursesWithLessons } = useQuery(
    ['public-teacher-courses-with-lessons', teacherId],
    async () => {
      if (!courses || courses.length === 0) return [];
      const promises = courses.map(async (course) => {
        try {
          const res = await studentService.getCourseLessons(course.id);
          const lessons = res.data?.lessons || res.data || [];
          return { ...course, lessons };
        } catch (err) {
          return { ...course, lessons: [] };
        }
      });
      return Promise.all(promises);
    },
    { enabled: !!courses, retry: false, staleTime: 60 * 1000 }
  );

  // For public/student views rely on `lesson.views` from `getCourseLessons`.
  // Avoid calling `getLessonAnalytics` here to prevent 403 responses for non-teacher users.

  // ✅ Mutation لتقييم الدروس
  const rateLessonMutation = useMutation(
    async ({ lessonId, rating, review }) => {
      const response = await studentService.rateLesson(lessonId, { rating, review });
      return response.data;
    },
    {
      onSuccess: (_data, { lessonId }) => {
        queryClient.invalidateQueries(['teacher-courses', teacherId]);
        if (lessonId) {
          queryClient.invalidateQueries(['lesson', lessonId]);
          queryClient.refetchQueries(['lesson', lessonId]);
        }
        toast.success('شكرًا لتقييمك! تم حفظه بنجاح');
        setRatingModal({ isOpen: false, type: 'teacher', lessonId: null, currentRating: 0, review: '' });
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'فشل تسجيل التقييم');
      }
    }
  );

  // ✅ دوال التعامل مع التقييم
  const submitRating = () => {
    if (ratingModal.currentRating === 0) {
      toast.error('يرجى اختيار تقييم من 1 إلى 5 نجوم');
      return;
    }

    if (ratingModal.type === 'lesson') {
      // تقييم الدرس
      if (ratingModal.lessonId) {
        rateLessonMutation.mutate({
          lessonId: ratingModal.lessonId,
          rating: ratingModal.currentRating,
          review: ratingModal.review.trim()
        });
      }
    } else {
      // تقييم المدرس
      submitTeacherRating();
    }
  };

  const submitTeacherRating = async () => {
    try {
      await studentService.rateTeacher(teacherId, {
        rating: ratingModal.currentRating,
        review: ratingModal.review.trim()
      });
      
      setRatingModal({ isOpen: false, type: 'teacher', lessonId: null, currentRating: 0, review: '' });
      toast.success('شكرًا لك! تم إرسال تقييمك بنجاح');
      
      // إعادة تحميل بيانات المدرس لتحديث التقييم
      await refetchTeacher();
    } catch (error) {
      console.error('Error rating teacher:', error);
      toast.error(error.response?.data?.error || 'فشل في إرسال التقييم، يرجى المحاولة مرة أخرى');
    }
  };

  // معالجات الكورسات
  const handleViewCourse = (courseId) => {
    navigate(`/courses/${courseId}`);
  };

  const handleSubscribe = (courseId) => {
    const courseData = courses?.find(course => course.id === courseId);
    
    if (!courseData) {
      toast.error('بيانات الكورس غير متاحة');
      return;
    }
    
    const subjectName = courseData.subject_name || 'مادة غير محدد';
    const courseTitle = courseData.title || 'كورس غير محدد';
    const teacherName = courseData.teacher_name || 'مدرس غير محدد';
    const price = courseData.price || 0;
    const durationText = getDurationTextFromDays(courseData.default_duration_days);
    
    const message = encodeURIComponent(
      `📚 طلب اشتراك في كورس\n\n` +
      `📖 المادة: ${subjectName}\n` +
      `📚 الكورس: ${courseTitle}\n` +
      `👨‍🏫 المدرس: ${teacherName}\n` +
      `💰 السعر: ${price} جنية ${durationText ? `(${durationText})` : ''}\n\n` +
      `🔸 معرف الطالب: ${user?.id || 'غير محدد'}\n` +
      `🔸 اسم الطالب: ${user?.name || 'غير محدد'}\n` +
      `🔸 البريد الإلكتروني: ${user?.email || 'غير محدد'}\n\n` +
      `يرغب في الاشتراك في هذا الكورس المحدد فقط. يرجى إرسال كود التفعيل بعد تأكيد التحويل.`
    );
    
    // استخدام الإعدادات المركزية للمنصة
    const whatsappUrl = getWhatsAppLink(message);
    window.open(whatsappUrl, '_blank');
    toast.success('جاري تحويلك إلى واتساب لإتمام عملية الدفع');
  };

  // ✅ Star Rating Component
  const StarRating = ({ rating, onRate, size = 'small' }) => {
    const starSize = size === 'large' ? 'w-12 h-12' : 'w-6 h-6';
    const stars = [1, 2, 3, 4, 5];
    
    return (
      <div className="flex gap-1 justify-center">
        {stars.map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onRate(star)}
            className={`${starSize} transition-all duration-200 ${
              star <= rating 
                ? 'text-yellow-400 hover:text-yellow-500 scale-110' 
                : 'text-gray-300 hover:text-yellow-400 hover:scale-110'
            }`}
          >
            <Star
              className={`${starSize} ${star <= rating ? 'fill-yellow-400' : ''}`}
              strokeWidth={2}
            />
          </button>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50/50">
        <LoadingSpinner size="large" />
        <p className="mt-4 text-gray-500 font-black animate-pulse uppercase tracking-widest text-xs">جاري تجهيز ملف المدرس...</p>
      </div>
    );
  }

  if (error || !teacherData) {
    return (
      <div className="max-w-xl mx-auto py-20 px-6 text-center animate-in fade-in zoom-in duration-500">
        <div className="bg-white rounded-[3rem] shadow-2xl p-12 border border-gray-100 relative overflow-hidden">
          <div className="absolute top-0 left-0 w-full h-2 bg-primary-600"></div>
          <Info className="w-16 h-16 text-primary-500 mx-auto mb-6" />
          <h2 className="text-2xl font-black text-gray-900 mb-4">المدرس غير متاح حالياً</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">نعتذر، يبدو أن المدرس الذي تحاول الوصول إليه غير موجود أو تم تغيير إعدادات خصوصية حسابه.</p>
          <button 
            onClick={() => navigate('/teachers')}
            className="bg-primary-600 text-white px-10 py-4 rounded-2xl font-black hover:bg-primary-700 transition-all flex items-center justify-center gap-2 mx-auto shadow-lg shadow-primary-100"
          >
            <ArrowRight size={18} /> دليل المدرسين المعتمدين
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50/30 pb-20 relative">
      {/* Decorative Blur Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary-100/20 rounded-full blur-[120px] -z-10"></div>
      <div className="absolute bottom-20 left-0 w-[400px] h-[400px] bg-purple-100/20 rounded-full blur-[100px] -z-10"></div>
      
      {/* Premium Sticky Header */}
      <div className="bg-white/80 backdrop-blur-xl border-b border-gray-100 sticky top-0 z-[100] transition-all duration-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => navigate('/teachers')}
              className="flex items-center gap-2 text-gray-500 hover:text-primary-600 font-black transition-all group px-4 py-2 rounded-xl hover:bg-primary-50"
            >
              <ArrowRight className="group-hover:translate-x-1 transition-transform" /> 
              دليل المدرسين
            </button>
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-2 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-100">
                    <ShieldCheck size={16} className="text-emerald-600" />
                    <span className="text-[10px] font-black text-emerald-700 uppercase tracking-widest hidden sm:block">Verified Educator</span>
                </div>
                <button className="p-2.5 text-gray-400 hover:text-primary-600 bg-gray-50 rounded-xl transition-all hover:rotate-12">
                    <Share2 size={18} />
                </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        
        {/* Modern Hero Card - Layout Fix Applied */}
        <div className="bg-white rounded-[2.5rem] sm:rounded-[3.5rem] shadow-2xl shadow-gray-200/50 border border-gray-100 overflow-hidden mb-12 animate-in fade-in slide-in-from-top-4 duration-700">
          <div className="relative">
            {/* Profile Info - Corrected Paddings to prevent overlap */}
            <div className="px-6 sm:px-10 lg:px-16 pb-12 relative">
              <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-center lg:items-end -mt-16 sm:-mt-24 lg:-mt-32">
                
                {/* Large Avatar Section */}
                <div className="relative group z-20">
                  <div className="absolute -inset-1.5 bg-gradient-to-tr from-primary-600 to-indigo-600 rounded-[2.8rem] blur opacity-25 group-hover:opacity-50 transition-opacity duration-500"></div>
                  <div className="relative w-40 h-40 sm:w-44 sm:h-44 lg:w-56 lg:h-56 rounded-[2.5rem] overflow-hidden border-[8px] border-white shadow-2xl bg-white">
                    {teacher.avatar ? (
                      <img
                        src={getImageUrl(teacher.avatar)}
                        alt={teacher.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        onError={(e) => { e.target.onerror = null; /* hide broken image */ e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-100 flex items-center justify-center text-4xl font-black text-gray-700">
                        {teacher.name?.charAt(0) || 'T'}
                      </div>
                    )}
                  </div>
                  <div className="absolute -bottom-3 -right-3 bg-primary-600 text-white p-2.5 sm:p-3.5 rounded-2xl sm:rounded-3xl border-4 sm:border-[6px] border-white shadow-xl z-30">
                    <Sparkles size={20} />
                  </div>
                </div>

                {/* Info Content - Padding adjusted for large screens */}
                <div className="flex-1 space-y-6 pt-4 lg:pt-36 w-full text-center lg:text-right relative z-10">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-8">
                    <div className="space-y-4">
                      <h1 className="text-2xl sm:text-3xl lg:text-4xl font-black text-gray-900 tracking-tight flex flex-wrap items-center justify-center lg:justify-start gap-3">
                        {teacher.name}
                        <CheckCircle className="text-blue-500 fill-blue-50" size={28} />
                      </h1>
                      <div className="flex flex-wrap items-center justify-center lg:justify-start gap-3">
                        <div className="bg-primary-50 text-primary-700 px-5 py-2 rounded-2xl font-black text-xs uppercase tracking-widest border border-primary-100 flex items-center gap-2 shadow-sm">
                          <BookOpen size={16} />
                          مدرس {teacher.subject_name || 'تخصص علمي'}
                        </div>
                        <div className="bg-emerald-50 text-emerald-700 px-5 py-2 rounded-2xl font-black text-xs uppercase tracking-widest border border-emerald-100 flex items-center gap-2 shadow-sm">
                          <MapPin size={16} />
                          {teacher.address || 'جميع المحافظات'}
                        </div>
                      </div>
                    </div>
                    
                    <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-end">
                      <button 
                        onClick={() => setActiveTab('video')}
                        className="bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 px-6 sm:px-8 py-3 sm:py-4.5 rounded-[1.5rem] sm:rounded-[1.8rem] font-black text-xs sm:text-sm border border-blue-200 hover:from-blue-100 hover:to-indigo-100 hover:shadow-2xl hover:scale-105 transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3 shadow-lg shadow-blue-100/50 group"
                      >
                        <PlayCircle size={18} className="sm:w-5 sm:h-5 group-hover:scale-110 transition-transform" />
                        الفيديو التعريفي
                      </button>
                      {/* ✅ زر تقييم المدرس */}
                      {user && user.role === 'student' && (
                        <button 
                          onClick={() => setRatingModal({ isOpen: true, type: 'teacher', lessonId: null, currentRating: 0, review: '' })}
                          className="bg-gradient-to-r from-amber-500 to-orange-500 text-white px-4 py-2 rounded-[1.2rem] font-black text-xs border border-amber-600 hover:from-amber-400 hover:to-orange-400 hover:shadow-2xl hover:scale-105 hover:shadow-amber-200/50 transition-all duration-300 flex items-center justify-center gap-1 shadow-lg group"
                        >
                          <Star size={16} className="group-hover:rotate-12 transition-transform fill-white/20" />
                          قيم المدرس
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Contact Grid - Modern Layout */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 pt-8 border-t border-gray-50">
                    <div className="flex items-center gap-3 text-gray-500 group justify-center lg:justify-start">
                      <div className="w-10 h-10 bg-primary-50 rounded-xl flex items-center justify-center text-primary-600 group-hover:bg-primary-600 group-hover:text-white transition-all shadow-sm">
                        <Mail size={18} />
                      </div>
                      <span className="text-sm font-bold truncate max-w-[150px]">{teacher.email || 'Private Email'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500 group justify-center lg:justify-start">
                      <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-all shadow-sm">
                        <Phone size={18} />
                      </div>
                      <span className="text-sm font-bold tracking-widest">{teacher.phone || 'Private Number'}</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-500 group justify-center lg:justify-start">
                      <div className="w-10 h-10 bg-indigo-50 rounded-xl flex items-center justify-center text-indigo-600 group-hover:bg-indigo-600 group-hover:text-white transition-all shadow-sm">
                        <Globe size={18} />
                      </div>
                      <span className="text-sm font-black uppercase tracking-tighter">Online Desk</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats Dashboard Analytics Bar */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {[
                { label: 'كورس متاح', value: teacher.stats?.course_count || 0, icon: BookOpen, color: 'text-blue-600', bg: 'bg-blue-50' },
                { label: 'تقييم الطلاب', value: teacher.stats?.avg_rating || '4.8', icon: Star, color: 'text-amber-500', bg: 'bg-amber-50', isRating: true },
                { label: 'سنوات الخبرة', value: teacher.experience_years || '7', icon: GraduationCap, color: 'text-purple-600', bg: 'bg-purple-50' }
            ].map((stat, i) => (
                <div key={i} className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex items-center gap-6 group hover:shadow-2xl transition-all duration-500">
                    <div className={`p-5 ${stat.bg} ${stat.color} rounded-3xl group-hover:scale-110 transition-transform shadow-inner`}>
                        <stat.icon size={28} />
                    </div>
                    <div>
                        <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.3em] mb-1">{stat.label}</p>
                        <div className="flex items-center gap-1">
                            <span className="text-4xl font-black text-gray-900 tracking-tighter">{stat.value}</span>
                            {stat.isRating && <span className="text-lg font-bold text-gray-300">/ 5</span>}
                        </div>
                    </div>
                </div>
            ))}
        </div>

        {/* Detailed Content Tabs Section */}
        <div className="bg-white rounded-[3rem] shadow-xl border border-gray-100 overflow-hidden min-h-[600px] mb-20">
          <div className="bg-gray-50/50 border-b border-gray-100 p-2 sm:p-4">
            <nav className="flex flex-wrap gap-2 justify-center lg:justify-start" aria-label="Tabs">
              {[
                  { id: 'about', label: 'السيرة المهنية', icon: User },
                  { id: 'courses', label: 'المحتوى الدراسي', icon: Layers },
                  { id: 'video', label: 'العرض التعريفي', icon: Video }
              ].map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex-1 lg:flex-none min-w-[140px] py-4 sm:py-5 px-6 sm:px-10 font-black text-xs sm:text-sm flex items-center justify-center gap-3 rounded-[1.8rem] transition-all duration-500 ${
                    activeTab === tab.id
                        ? 'bg-white text-gray-900 shadow-xl border border-gray-100 translate-y-[-2px]'
                        : 'text-gray-400 hover:text-gray-600 hover:bg-white/50'
                    }`}
                >
                    <div className={`p-1.5 rounded-lg ${activeTab === tab.id ? 'bg-primary-600 text-white shadow-lg' : 'bg-gray-100'}`}>
                        <tab.icon size={16} />
                    </div>
                    {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6 sm:p-10 lg:p-16">
            {/* About Tab - Content Grid */}
            {activeTab === 'about' && (
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 animate-in fade-in duration-700">
                <div className="lg:col-span-7 space-y-10">
                    <div className="space-y-6">
                        <div className="flex items-center gap-3">
                            <div className="w-1.5 h-8 bg-primary-600 rounded-full"></div>
                            <h3 className="text-2xl font-black text-gray-900 tracking-tight uppercase">نبذة عن المحاضر</h3>
                        </div>
                        <div className="bg-gray-50/50 p-8 rounded-[2.5rem] border border-gray-100 relative group overflow-hidden">
                            <div className="absolute -top-6 -left-6 w-24 h-24 bg-primary-600/5 rounded-full blur-2xl"></div>
                            <p className="text-gray-600 leading-loose text-lg font-medium italic relative z-10">
                                "{teacher.bio || 'لم يتم إضافة نبذة تعريفية رسمية من قبل المدرس حتى الآن، لكنه يعد أحد الكوادر المتميزة في تخصصه التعليمي بالمنصة.'}"
                            </p>
                        </div>
                    </div>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="p-8 rounded-[2.2rem] bg-gradient-to-br from-blue-50 to-white border border-blue-100 shadow-sm space-y-4 group hover:bg-white hover:shadow-xl transition-all duration-500">
                            <div className="w-12 h-12 bg-blue-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-blue-100 group-hover:rotate-12 transition-transform">
                                <Target size={24} />
                            </div>
                            <h4 className="text-lg font-black text-gray-900">فلسفة التعلم</h4>
                            <p className="text-sm text-gray-500 leading-relaxed font-medium">نتبع منهجية قائمة على ربط المعلومة بالواقع العملي وتبسيط المفاهيم لتناسب كافة المستويات الطلابية.</p>
                        </div>

                        <div className="p-8 rounded-[2.2rem] bg-gradient-to-br from-purple-50 to-white border border-purple-100 shadow-sm space-y-4 group hover:bg-white hover:shadow-xl transition-all duration-500">
                            <div className="w-12 h-12 bg-purple-600 text-white rounded-2xl flex items-center justify-center shadow-lg shadow-purple-100 group-hover:rotate-12 transition-transform">
                                <Lightbulb size={24} />
                            </div>
                            <h4 className="text-lg font-black text-gray-900">طرق التدريس</h4>
                            <p className="text-sm text-gray-500 leading-relaxed font-medium">استخدام الخرائط الذهنية، حل الامتحانات التفاعلية، والمتابعة الدورية عبر منصات التواصل.</p>
                        </div>
                    </div>
                </div>
                
                <div className="lg:col-span-5 space-y-8">
                    <div className="bg-white rounded-[2.5rem] border border-gray-100 p-8 shadow-sm group">
                        <h3 className="text-lg font-black text-gray-900 mb-8 flex items-center gap-3">
                            <GraduationCap className="text-primary-600" /> المسيرة الأكاديمية
                        </h3>
                        <div className="space-y-6">
                            {[
                                { text: teacher.education || 'خريج كليات التربية المتخصصة', label: 'المؤهل العلمي' },
                                { text: teacher.specialization || 'خبير معتمد في المنهج التعليمي', label: 'التخصص الدقيق' },
                                { text: teacher.qualifications || 'حاصل على شهادات تدريبية متقدمة', label: 'الاعتمادات' }
                            ].map((item, idx) => (
                                <div key={idx} className="flex items-start gap-4 p-4 rounded-2xl hover:bg-gray-50 transition-colors border border-transparent hover:border-gray-100">
                                    <div className="w-2 h-2 bg-emerald-500 rounded-full mt-2.5 flex-shrink-0"></div>
                                    <div>
                                        <p className="text-[10px] font-black text-primary-500 uppercase tracking-widest mb-1">{item.label}</p>
                                        <p className="text-sm font-bold text-gray-700 leading-snug">{item.text}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="bg-gray-900 rounded-[2.5rem] p-10 text-white relative overflow-hidden group shadow-2xl">
                        <div className="absolute -top-10 -right-10 w-40 h-40 bg-primary-600/20 rounded-full blur-3xl group-hover:scale-150 transition-transform duration-700"></div>
                        <div className="relative z-10 space-y-6 text-center">
                            <div className="bg-white/10 w-16 h-16 rounded-2xl flex items-center justify-center mx-auto shadow-xl backdrop-blur-md group-hover:scale-110 transition-transform">
                                <Heart className="text-rose-500 fill-rose-500" size={32} />
                            </div>
                            <div className="space-y-2">
                                <h4 className="text-xl font-black">كلمة للطلاب</h4>
                                <p className="text-gray-400 text-sm leading-relaxed font-medium">"النجاح ليس محطة وصول، بل هو رحلة مستمرة من العمل والشغف. نحن هنا لنضيء لك طريق التفوق خطوة بخطوة."</p>
                            </div>
                        </div>
                    </div>
                </div>
              </div>
            )}

            {/* Courses Tab - Professional Grid */}
            {activeTab === 'courses' && (
              <div className="space-y-12 animate-in fade-in duration-700">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 px-2">
                    <div className="space-y-1">
                        <h3 className="text-2xl font-black text-gray-900 flex items-center gap-3 uppercase tracking-tighter">
                            <div className="w-2 h-8 bg-emerald-500 rounded-full"></div>
                            الكورسات والبرامج المتاحة
                        </h3>
                        <p className="text-gray-400 text-sm font-medium mr-5">تصفح أقوى العروض التعليمية المتاحة حالياً للتسجيل</p>
                    </div>
                    <div className="bg-emerald-50 text-emerald-700 px-6 py-2.5 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-emerald-100 shadow-sm flex items-center gap-2">
                        <ShieldCheck size={14} /> {courses.length} كورس معتمد
                    </div>
                </div>

                {courses.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 pb-10">
                    {courses.map((course) => {
                      const cw = (coursesWithLessons || []).find(c => c.id === course.id) || course;
                      const vids = (cw.lessons || []);
                      const courseViews = vids.reduce((sum, v) => sum + (parseInt(v?.views || 0, 10) || 0), 0);

                      return (
                        <ExploreCoursesCard
                          key={course.id}
                          course={{ ...course, lessons: cw.lessons || [], courseViews }}
                          onViewCourse={handleViewCourse}
                          onSubscribe={handleSubscribe}
                          formatTotalDuration={formatTotalDuration}
                          getDurationTextFromDays={getDurationTextFromDays}
                        />
                      );
                    })}
                  </div>
                ) : (
                  <div className="text-center py-24 bg-gray-50/50 rounded-[3.5rem] border-2 border-dashed border-gray-200 mx-auto max-w-3xl">
                    <div className="w-20 h-20 bg-white rounded-[2rem] flex items-center justify-center mx-auto mb-6 shadow-xl border border-gray-100">
                        <BookOpen size={40} className="text-gray-200" />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-2">لا توجد كورسات عامة حالياً</h3>
                    <p className="text-gray-400 max-w-xs mx-auto font-medium">المدرس بصدد تجهيز محتوى تعليمي متميز للطلبة، انتظرونا قريباً.</p>
                    <button className="mt-8 text-primary-600 font-black text-xs uppercase tracking-[0.2em] flex items-center gap-2 mx-auto hover:gap-4 transition-all">
                        تلقي إشعار عند التوفر <ArrowRight size={14} className="rotate-180" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Video Tab - Presentation Mode */}
            {activeTab === 'video' && (
              <div className="max-w-5xl mx-auto space-y-12 animate-in fade-in zoom-in duration-700">
                <div className="text-center space-y-4">
                    <div className="bg-primary-50 text-primary-600 px-6 py-2 rounded-2xl text-[10px] font-black uppercase tracking-widest border border-primary-100 w-fit mx-auto shadow-sm">
                        Cinematic Showcase
                    </div>
                    <h3 className="text-3xl lg:text-5xl font-black text-gray-900 tracking-tight leading-tight">شاهد أسلوب الشرح والتبسيط</h3>
                    <p className="text-gray-500 max-w-xl mx-auto font-medium text-lg leading-relaxed">بإمكانك تكوين صورة كاملة عن الطريقة التعليمية للمحاضر قبل البدء في أي كورس دراسي.</p>
                </div>

                {teacher.intro_video ? (
                  (() => {
                    const youtubeEmbed = getYouTubeEmbedUrl(teacher.intro_video);
                    if (youtubeEmbed) {
                      return (
                        <div className="relative aspect-video rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100 bg-gray-900">
                          <div className="absolute inset-0 bg-gradient-to-br from-primary-600/10 to-indigo-600/10 z-0"></div>
                          <iframe
                            className="w-full h-full relative z-10"
                            src={youtubeEmbed}
                            title="Teacher Intro Video"
                            frameBorder="0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            onError={(e) => {
                              e.target.style.display = 'none';
                              const fallbackDiv = e.target.parentElement;
                              fallbackDiv.innerHTML = `
                                <div class="w-full h-full flex flex-col items-center justify-center bg-gray-900 rounded-[3rem]">
                                  <div class="text-white text-center p-8">
                                    <div class="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
                                      <svg class="w-8 h-8 text-white" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                      </svg>
                                    </div>
                                    <p class="text-lg font-black mb-2">لا يمكن تشغيل الفيديو هنا</p>
                                    <p class="text-sm text-gray-300 mb-4">شاهد الفيديو مباشرة على المصدر</p>
                                    <a href="${teacher.intro_video}" target="_blank" class="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-xl font-black hover:bg-red-700 transition-all">
                                      <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                                      </svg>
                                      شاهد المصدر
                                    </a>
                                  </div>
                                </div>
                              `;
                            }}
                          />
                        </div>
                      );
                    }

                    // If not a YouTube link, assume direct video URL (CDN) and render <video>
                    if (typeof teacher.intro_video === 'string' && teacher.intro_video.startsWith('http')) {
                      return (
                        <div className="relative aspect-video rounded-[2rem] overflow-hidden shadow-2xl border border-gray-100 bg-gray-900">
                          <video className="w-full h-full object-cover" controls src={teacher.intro_video} />
                        </div>
                      );
                    }

                    return null;
                  })()
                ) : (
                  <div className="text-center py-32 bg-gray-50/50 rounded-[4rem] border-2 border-dashed border-gray-200 px-8 relative overflow-hidden group">
                    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-primary-100/10 rounded-full blur-[100px] pointer-events-none"></div>
                    <div className="w-24 h-24 bg-white rounded-[2.2rem] shadow-xl flex items-center justify-center mx-auto mb-8 border border-gray-100 relative z-10 group-hover:rotate-12 transition-transform duration-500">
                        <Video size={48} className="text-gray-200" />
                    </div>
                    <h3 className="text-3xl font-black text-gray-900 mb-4 tracking-tight relative z-10">الفيديو التعريفي سيظهر هنا</h3>
                    <p className="text-gray-400 mb-12 max-w-sm mx-auto font-medium text-lg leading-relaxed relative z-10">المدرس يقوم حالياً بتصوير العرض التعريفي الخاص به، يمكنك البدء باستكشاف الكورسات مباشرة.</p>
                    <button 
                        onClick={() => navigate('/contact')}
                        className="bg-primary-600 text-white px-12 py-5 rounded-[2rem] font-black hover:bg-primary-700 transition-all flex items-center gap-3 mx-auto shadow-2xl shadow-primary-200 transform active:scale-95 relative z-10"
                    >
                      <HelpCircle size={22} /> طرح استفسار تقني
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Dynamic Footer Support Area */}
        <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-primary-950 rounded-[3.5rem] p-8 lg:p-16 text-white relative overflow-hidden shadow-[0_50px_100px_rgba(0,0,0,0.2)]">
            <div className="absolute top-[-20%] right-[-10%] w-[500px] h-[500px] bg-primary-600/10 rounded-full blur-[120px] -z-0"></div>
            <div className="absolute bottom-[-10%] left-[-10%] w-[300px] h-[300px] bg-indigo-500/10 rounded-full blur-[100px] -z-0"></div>
            
            <div className="relative z-10 flex flex-col md:flex-row items-center justify-between gap-16">
                <div className="text-center md:text-right space-y-6 flex-1">
                    <div className="bg-primary-600 w-16 h-16 rounded-[1.8rem] flex items-center justify-center shadow-2xl mb-6 mx-auto md:mr-0 animate-bounce">
                        <MessageCircle size={32} />
                    </div>
                    <h3 className="text-2xl lg:text-3xl font-black leading-tight tracking-tight">هل لديك أي سؤال <br /> بخصوص هذا المدرس؟</h3>
                    <p className="text-gray-400 font-medium text-sm max-w-xl leading-relaxed">فريق الدعم الموحد جاهز للرد على كافة استفساراتك بخصوص طرق الدفع، مواعيد الدروس، وكود التفعيل في أي وقت.</p>
                </div>
                <div className="flex flex-col gap-4 w-full md:w-auto">
                    <button className="bg-white text-gray-900 px-14 py-6 rounded-[2.2rem] font-black text-lg shadow-2xl hover:bg-primary-50 transition-all transform active:scale-95 flex items-center justify-center gap-4">
                        تحدث معنا الآن
                        <ChevronLeft size={24} />
                    </button>
                    <p className="text-[10px] text-center font-black text-gray-500 uppercase tracking-[0.3em]">متوفرون على مدار 24 ساعة</p>
                </div>
            </div>
        </div>
    </div>

    {/* Rating Modal - Supports both Teacher and Lesson Rating */}
    {ratingModal.isOpen && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-[300] p-4 animate-in fade-in duration-300">
            <div className="bg-white rounded-[3rem] shadow-2xl p-10 max-w-md w-full relative overflow-hidden transition-all transform scale-100">
                <button 
                    onClick={() => setRatingModal({ isOpen: false, type: 'teacher', lessonId: null, currentRating: 0, review: '' })}
                    className="absolute top-8 left-8 text-gray-300 hover:text-red-500 transition-colors bg-gray-50 p-2 rounded-xl"
                >
                    <X size={20} />
                </button>

                <div className="text-center mb-8">
                    <div className="w-20 h-20 bg-primary-50 rounded-full flex items-center justify-center mx-auto mb-6">
                        <Star className="text-primary-600" size={40} />
                    </div>
                    <h3 className="text-2xl font-black text-gray-900 mb-2">
                        {ratingModal.type === 'lesson' ? 'قيم الدرس' : 'قيم المدرس'}
                    </h3>
                    <p className="text-gray-600 text-lg">
                        {ratingModal.type === 'lesson' 
                            ? 'شاركنا رأيك في هذا الدرس' 
                            : `شاركنا رأيك في ${teacher.name}`
                        }
                    </p>
                </div>
            
                <div className="py-6 flex justify-center scale-125">
                    <StarRating
                        rating={ratingModal.currentRating}
                        onRate={(rating) => setRatingModal(prev => ({ ...prev, currentRating: rating }))}
                        size="large"
                    />
                </div>

                {ratingModal.type === 'teacher' && (
                    <div className="mb-8">
                        <label className="block text-sm font-bold text-gray-700 mb-2">مراجعة (اختياري)</label>
                        <textarea
                            value={ratingModal.review}
                            onChange={(e) => setRatingModal(prev => ({ ...prev, review: e.target.value }))}
                            placeholder="اكتب انطباعك عن المدرس..."
                            className="w-full p-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                            rows="4"
                            maxLength="1000"
                        />
                    </div>
                )}

                {ratingModal.type === 'lesson' && (
                    <div className="mb-8">
                        <label className="block text-sm font-bold text-gray-700 mb-2">مراجعة (اختياري)</label>
                        <textarea
                            value={ratingModal.review}
                            onChange={(e) => setRatingModal(prev => ({ ...prev, review: e.target.value }))}
                            placeholder="اكتب انطباعك عن هذا الدرس..."
                            className="w-full p-4 border border-gray-200 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                            rows="4"
                            maxLength="1000"
                        />
                    </div>
                )}

                <div className="bg-gray-50 p-4 rounded-2xl mb-8">
                    <p className="text-xs font-bold text-gray-500">
                        {ratingModal.currentRating === 0 ? 'يرجى اختيار عدد النجوم للتقييم' : `لقد اخترت ${ratingModal.currentRating} من 5 نجوم`}
                    </p>
                </div>

                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => setRatingModal({ isOpen: false, type: 'teacher', lessonId: null, currentRating: 0, review: '' })}
                        className="px-6 py-4 border border-gray-100 rounded-2xl text-gray-400 font-black text-sm hover:bg-gray-50 transition-all uppercase tracking-widest"
                    >
                        تجاهل
                    </button>
                    <button
                        onClick={submitRating}
                        disabled={ratingModal.currentRating === 0 || (ratingModal.type === 'lesson' && rateLessonMutation.isLoading)}
                        className="px-6 py-4 bg-primary-600 text-white rounded-2xl font-black text-sm hover:bg-primary-700 transition-all uppercase tracking-widest disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                        {(ratingModal.type === 'lesson' && rateLessonMutation.isLoading) ? <LoadingSpinner size="small" /> : <Star size={16} />}
                        إرسال التقييم
                    </button>
                </div>
            </div>
        </div>
      )}
</div>
  );
};

export default TeacherProfile;